import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient, type AppError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { useState } from 'react';
import { toast } from 'sonner';

type ResetPasswordForm = { newPassword: string };

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const token = searchParams.get('token');

  const schema = z.object({
    newPassword: z
      .string()
      .min(8, t('validation.password_min'))
      .regex(/[A-Z]/, t('validation.password_uppercase'))
      .regex(/[a-z]/, t('validation.password_lowercase'))
      .regex(/[0-9]/, t('validation.password_number')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: ResetPasswordForm) => {
    setError(null);
    try {
      await apiClient<{ message: string }>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword: data.newPassword }),
        skipAuth: true,
      });
      toast.success(t('auth.reset_success'));
      navigate('/login');
    } catch (err) {
      const appError = err as AppError;
      if (appError.code === 'INVALID_RESET_TOKEN') {
        setError(t('auth.error_invalid_reset_token'));
      } else {
        setError(t('auth.error_generic'));
      }
    }
  };

  if (!token) {
    return (
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {t('auth.error_invalid_reset_token')}
          </div>
          <Link
            to="/forgot-password"
            className="block text-center text-sm font-medium text-primary hover:underline"
          >
            {t('auth.reset_password')}
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4 pt-6">
          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="newPassword">{t('auth.new_password')}</Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              {...register('newPassword')}
            />
            {errors.newPassword && (
              <p className="text-xs text-destructive">{errors.newPassword.message}</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('common.loading') : t('auth.reset_password')}
          </Button>
        </CardContent>
        <CardFooter className="text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-primary hover:underline">
            {t('auth.login')}
          </Link>
        </CardFooter>
      </form>
    </Card>
  );
}
